import { Fetcher } from '@this/src/util';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import _ from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment';
import Notification from '@this/src/notification';
import type { HotelOrderItemRakutenTotalArgs } from '@this/src/domain/arrangement/hotel_order_item_rakuten_total';
import HotelOrderItemRakutenTotal, {
  convertHotelOrderItemRakutenTotal
} from '@this/src/domain/arrangement/hotel_order_item_rakuten_total';
import type { ArrangerArgs } from '@this/src/domain/arranger/arranger';
import Arranger from '@this/src/domain/arranger/arranger';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import TotalSearchArea from './total_search_area';
import TotalResultTable from './total_result_table';

interface RakutenTotalResponse {
  records: HotelOrderItemRakutenTotalArgs[];
  arrangers: ArrangerArgs[];
  supplied_item_id: number | null;
  supplied_item_name: string | null;
  current_arranger: ArrangerArgs;
  total_count: number;
  total_page: number;
}

const Total = observer(() => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [rakutenTotals, setRakutenTotals] = useState<HotelOrderItemRakutenTotal[]>([]);
  const [arrangers, setArrangers] = useState<Arranger[]>([]);
  const [currentArranger, setCurrentArranger] = useState<Arranger | null>(null);
  const [suppliedItemId, setSuppliedItemId] = useState<number | undefined>(undefined);
  const [suppliedItemName, setSuppliedItemName] = useState<string>('');
  const [receiveDateFrom, setReceiveDateFrom] = useState<Moment | undefined>(
    utils.getParamMoment('receive_date_from') ?? moment()
  );
  const [receiveDateTo, setReceiveDateTo] = useState<Moment | undefined>(
    utils.getParamMoment('receive_date_to') ?? undefined
  );
  const fetchingRef = useRef(false);

  const fetchTodos = useCallback(() => {
    if (fetchingRef.current) {
      fetchingRef.current = false;
      return;
    }
    setIsLoading(true);
    setRakutenTotals([]);

    const params = {
      receive_date_from: receiveDateFrom?.format('YYYY-MM-DD'),
      receive_date_to: receiveDateTo?.format('YYYY-MM-DD')
    };
    _.each(params, (value, key) => {
      if (value === undefined) {
        delete params[key as keyof typeof params];
      }
    });
    const queryParams = new URLSearchParams();
    queryParams.set('tab', 'AUTO_RESERVATION_INVENTORY');
    queryParams.set('sub_tab', 'RAKUTEN_HOTEL');
    queryParams.set('rakuten_hotel_type', 'TOTAL');
    if (receiveDateFrom) {
      queryParams.set('receive_date_from', receiveDateFrom.format('YYYY-MM-DD'));
    }
    if (receiveDateTo) {
      queryParams.set('receive_date_to', receiveDateTo.format('YYYY-MM-DD'));
    }
    history.push(`/arrangement/todo_list?${queryParams.toString()}`);

    Fetcher.get<RakutenTotalResponse>('/arrangement/auto_reservation_inventory/rakuten_hotel/total.json', params)
      .then(res => {
        setRakutenTotals(
          res.records.map(args => new HotelOrderItemRakutenTotal(convertHotelOrderItemRakutenTotal(args)))
        );
        setArrangers(res.arrangers.map(args => new Arranger(args)));
        setSuppliedItemId(res.supplied_item_id || undefined);
        setSuppliedItemName(res.supplied_item_name || '');
        setCurrentArranger(new Arranger(res.current_arranger));
      })
      .catch(e => {
        Notification.error('データの取得に失敗しました');
      })
      .finally(() => setIsLoading(false));
  }, [receiveDateFrom, receiveDateTo, history]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  return (
    <>
      <TotalSearchArea
        receiveDateFrom={receiveDateFrom}
        receiveDateTo={receiveDateTo}
        suppliedItemName={suppliedItemName}
        setReceiveDateFrom={setReceiveDateFrom}
        setReceiveDateTo={setReceiveDateTo}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <TotalResultTable
          hotelOrderItemRakutenTotals={rakutenTotals}
          arrangers={arrangers}
          currentArranger={currentArranger}
          suppliedItemId={suppliedItemId || null}
        />
      )}
    </>
  );
});

export default Total;
